import { Settings } from '@streamr/tv-engine/~/settings';
import { addMediaStreamTrackStopListener } from '../../utils/addMediaStreamTrackStopListener';
import * as audioRecorderProcessor from '../mediaproducer/workers/webaudiorecorder.worklet';
import { Timekeeper } from './Timekeeper';
import { Track } from './Track';
export class AudioFrameExtractor {
    static initialized = false;
    static audioContext;
    track;
    trackId;
    audioSource;
    audioWorklet;
    performanceNowAtFirstFrameMicros = -1;
    frameCounter = 0;
    isStopped = false;
    removeTrackStopListener;
    timestampOffsetMicros;
    outputTrack;
    constructor(track) {
        this.track = track;
        this.trackId = track.id;
        this.outputTrack = new Track(track.id, true);
    }
    static async initialize(audioContext) {
        if (AudioFrameExtractor.initialized)
            return;
        AudioFrameExtractor.audioContext = audioContext;
        await AudioFrameExtractor.audioContext.audioWorklet.addModule(
        // biome-ignore lint/suspicious/noExplicitAny: @todo fix types (the "any")
        audioRecorderProcessor);
        AudioFrameExtractor.initialized = true;
    }
    start() {
        if (!AudioFrameExtractor.initialized) {
            throw new Error('AudioFrameExtractor not yet initialized');
        }
        this.startFrameExtraction();
    }
    startFrameExtraction() {
        if (!AudioFrameExtractor.audioContext) {
            throw new Error('AudioContext not initialized');
        }
        // Create media stream source
        this.audioSource =
            AudioFrameExtractor.audioContext.createMediaStreamSource(new MediaStream([this.track]));
        // Create and configure worklet
        this.audioWorklet = new AudioWorkletNode(AudioFrameExtractor.audioContext, 'audio-recorder-processor');
        // Handle messages from the audio worklet
        this.audioWorklet.port.onmessage = (event) => {
            if (this.isStopped)
                return;
            if (event.data.samples) {
                if (this.performanceNowAtFirstFrameMicros === -1) {
                    this.performanceNowAtFirstFrameMicros = Math.round((performance.now() + performance.timeOrigin) * 1_000);
                    console.log('WebAudioRecorder start() performanceNowAtFirstFrameMillis', this.performanceNowAtFirstFrameMicros / 1_000);
                }
                const timestamp = this.frameCounter * Settings.audioFrameDurationMicroseconds;
                const frame = this.createUnencodedAudioFrame(timestamp, event.data.samples);
                this.outputTrack.emit(frame, false, [frame.data.buffer]);
            }
        };
        // Connect the audio graph
        this.audioSource.connect(this.audioWorklet);
        this.audioWorklet.connect(AudioFrameExtractor.audioContext.destination);
        // Listen for track stop/end to trigger cleanup
        this.removeTrackStopListener = addMediaStreamTrackStopListener(this.track, () => {
            console.log('AudioFrameExtractor: track stopped/ended');
            this.cleanup();
        });
    }
    cleanup() {
        if (this.isStopped)
            return;
        this.isStopped = true;
        console.log('AudioFrameExtractor: cleaning up');
        // Disconnect audio nodes
        if (this.audioSource) {
            this.audioSource.disconnect();
            this.audioSource = undefined;
        }
        if (this.audioWorklet) {
            this.audioWorklet.port.onmessage = null;
            this.audioWorklet.disconnect();
            this.audioWorklet = undefined;
        }
        // Remove track stop listener
        if (this.removeTrackStopListener) {
            this.removeTrackStopListener();
            this.removeTrackStopListener = undefined;
        }
        this.outputTrack.emit(undefined, true);
        this.outputTrack.unsetListener();
        console.log('AudioFrameExtractor: cleanup complete');
    }
    createUnencodedAudioFrame(originalTimestampMicros, data) {
        if (!this.timestampOffsetMicros) {
            this.timestampOffsetMicros =
                Timekeeper.getInstance().getLastTimestampMicros();
        }
        const timestampMicros = BigInt(originalTimestampMicros) + this.timestampOffsetMicros;
        Timekeeper.getInstance().setLastTimestampMicros(timestampMicros);
        return {
            frameNumber: BigInt(this.frameCounter++),
            timestamp: timestampMicros,
            duration: BigInt(Settings.audioFrameDurationMicroseconds),
            trackId: this.trackId ?? '',
            data: data,
            done: false,
            isKeyFrame: true,
        };
    }
    /**
     * Stop extraction and clean up resources.
     */
    stop() {
        if (this.isStopped) {
            return Promise.resolve();
        }
        console.log('AudioFrameExtractor: stop() called');
        this.cleanup();
        return Promise.resolve();
    }
    getOutputTrack() {
        return this.outputTrack;
    }
    getTrackId() {
        return this.trackId;
    }
}
